// What works while OFFLINE (offline mode enabled + backend unreachable).
// Single source of truth consumed by the nav, the route guard, action
// wrappers, and inline handler guards. Everything is available when the
// flag is off or the backend is reachable.

import { isOfflineModeEnabled } from './featureFlag';
import { connectivityMonitor } from './connectivityMonitor';

export const OFFLINE_CAPABILITIES = {
  'calendar.view': true,
  'calendar.statusTransition': true, // check-in, complete, no-show, cancel, undo
  'calendar.cashCopay': true,
  'calendar.create': false,
  'calendar.reschedule': false,
  'calendar.delete': false,
  'patient.view': true,
  'patient.list': true, // cached identity search (calendar window)
  'patient.create': false,
  'patient.edit': false,
  'notes.view': true,
  'notes.edit': true,
  'notes.sign': true,
  'notes.createFromAppointment': true,
  'flowsheet.edit': true,
  'billing.cardPayment': false,
  'billing.view': false,
  'messaging': false,
  'prescriptions': false,
  'forms.send': false,
  'reports': false,
};

// Routes that render usefully offline. Everything else gets the
// "Not available offline" page instead of a blank/errored screen.
export const OFFLINE_ROUTES = [
  /^\/$/,
  /^\/appointments\/calendar$/,
  /^\/appointments\/[0-9a-f-]+$/i, // appointment detail (status transitions)
  /^\/patients$/, // cached identity list/search
  /^\/patients\/[^/]+$/, // chart (cached)
  /^\/clinical-notes$/,
  /^\/clinical-notes\/new$/,
  /^\/clinical-notes\/[^/]+(\/edit)?$/,
  /^\/encounters\/[^/]+$/,
  /^\/visits\/[^/]+$/,
  /^\/profile$/,
];

// Sidebar items that stay ACTIVE offline; everything else renders disabled
// with a tooltip (never hidden — hiding reads as a bug to a front desk user).
export const OFFLINE_NAV_AVAILABLE = new Set([
  'Home',
  'Dashboard',
  'Scheduling',
  'Calendar View',
  'Calendar',
  'Appointments',
  'Patients',
  'Patient List',
  'Clinical Notes',
  'Help',
  'Profile',
]);

export const OFFLINE_UNAVAILABLE_MESSAGE = 'Not available offline';

function isOffline() {
  return isOfflineModeEnabled() && !connectivityMonitor.isOnline();
}

export function isCapabilityAvailable(key) {
  if (!isOffline()) return true;
  return OFFLINE_CAPABILITIES[key] === true;
}

export function isRouteAvailableOffline(pathname) {
  return OFFLINE_ROUTES.some((re) => re.test(pathname));
}

export function isNavAvailableOffline(name) {
  return OFFLINE_NAV_AVAILABLE.has(name);
}

// Throttled per-message so a drag loop or repeated clicks don't stack toasts.
const _lastNotified = new Map();
export function notifyOfflineUnavailable(message = OFFLINE_UNAVAILABLE_MESSAGE) {
  const now = Date.now();
  if ((_lastNotified.get(message) || 0) > now - 5000) return;
  _lastNotified.set(message, now);
  window.dispatchEvent(new CustomEvent('offline:action-unavailable', { detail: { message } }));
}
